import { t } from '@bia-auction/shared-i18n';
import type { ShowcaseStats } from '@bia-auction/shared-schema';
import { SHOWCASE_COUNTER_KEYS, showcaseStatsSchema } from '@bia-auction/shared-schema';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatCount } from '../lib/format';

/**
 * Marketing counters on the public showcase (M16, 11.08.2026 review row 7).
 *
 * Renders **nothing at all** unless an operator has switched a counter on: no heading, no
 * empty frame, no zeroes. The server omits disabled counters entirely, so "off" and "still
 * loading" and "request failed" all reach this component the same way — as no figures — and
 * all three should look identical to a visitor. A marketing band is decoration; a visitor
 * who never sees it has lost nothing, whereas one who sees it flash a skeleton or an error
 * has been shown the machinery.
 *
 * That is also why there is no error branch: `/showcase/stats` failing must not put a red
 * box above the auction list it has nothing to do with.
 *
 * **Presented as a brand-gradient band (M18).** It began as a small bordered strip, which
 * spent the page's one marketing moment on something that read as a form control. The group
 * site states the same figures — 50 kurumsal müşteri, 81 ilde hizmet — as a full-width
 * green-to-navy band, and that band is doing structural work: it is the only large brand-
 * coloured surface on a page that is otherwise navy chrome around white cards. Ours now
 * matches.
 *
 * No photograph behind this one, unlike the hero bands: `--grad-brand` is opaque, so an image
 * under it would be invisible — and the group site's own stats band is a flat gradient too.
 */
export function ShowcaseCounters() {
  const { data } = useQuery({
    queryKey: ['showcase-stats'],
    queryFn: () => api<ShowcaseStats>('/showcase/stats', { auth: false }, showcaseStatsSchema),
    // The server caches this for five minutes; matching that here keeps a visitor clicking
    // around the site from re-requesting a figure that cannot have moved.
    staleTime: 5 * 60_000,
  });

  const shown = SHOWCASE_COUNTER_KEYS.flatMap((key) => {
    const value = data?.[key];
    return value === undefined ? [] : [{ key, value }];
  });
  if (shown.length === 0) return null;

  return (
    <dl
      className="grid gap-6 rounded-lg bg-brand px-6 py-8 text-navy-800 sm:grid-cols-2 lg:grid-cols-3"
    >
      {shown.map(({ key, value }) => (
        <div key={key} className="text-center sm:text-left">
          {/* Figure first, label under it: the number is the claim, the label only says what
              it counts — the same order the group site uses. */}
          <dd className="font-head text-display font-semibold tabular-nums">
            {formatCount(value)}
          </dd>
          <dt className="text-sm font-medium">{t(`bidder.showcase.stats.${key}`)}</dt>
        </div>
      ))}
    </dl>
  );
}
